<template>
  <div class="mobile-about-container">
    <!-- Banner Section -->

       <section class="relative h-64 bg-cover bg-center" :style="{ backgroundImage: `url(${banner_about})` }">
      <div class="absolute inset-0 bg-black bg-opacity-40"></div>
      <div class="relative z-10 h-full flex items-center px-4">
        <div>
          <h1 class="font-bold text-[6vw] mb-2 text-white">{{ aboutTexts.company }}</h1>
          <p class="font-normal text-[3.5vw] leading-[1.4] px-2 text-white">
            {{ aboutTexts.description }}
          </p>
        </div>
      </div>
    </section>

    <!-- Business Segments -->
    <section class="bg-white py-8 px-4">
      <h2 class="text-[5vw] font-bold text-left mb-6">{{ businessTexts.title }}</h2>
      <div class="space-y-6">
        <div v-for="(segment, index) in segments" :key="index" 
             class="bg-gray-50 rounded-lg p-4 ">
          <div class="flex items-center mb-3">
            <img :src="segment.normalIcon" class="w-8 h-8 mr-3" />
            <h3 class="text-[4vw] font-bold text-blue-900">{{ segment.title }}</h3>
          </div>
          <p class="text-[3vw] text-gray-600 leading-[1.4]">{{ segment.content }}</p>
        </div>
      </div>
    </section>

    <!-- Mission / Vision / Values -->
    <section class="bg-[#F3F8FF] py-8 px-4">
      <!-- Mission -->
      <div class="mb-[10px] flex items-stretch h-32">
        <div class="w-1/2">
          <img :src="Mission" alt="Mission" class="w-full h-full object-cover" />
        </div>
        <div class="w-1/2 bg-white p-3 shadow-sm flex flex-col justify-center">
          <h3 class="text-[4vw] font-bold text-blue-900 mb-2">Mission</h3>
          <p class="text-[2.8vw] leading-[1.4] text-gray-700">{{ aboutTexts.mission }}</p>
        </div>
      </div>

      <!-- Vision -->
      <div class="mb-[10px] flex items-stretch h-32">
        <div class="w-1/2 bg-white p-3 shadow-sm flex flex-col justify-center">
          <h3 class="text-[4vw] font-bold text-blue-900 mb-2">Vision</h3>
          <p class="text-[2.8vw] leading-[1.4] text-gray-700">{{ aboutTexts.vision }}</p>
        </div>
        <div class="w-1/2">
          <img :src="Vision" alt="Vision" class="w-full h-full object-cover" />
        </div>
      </div>

      <!-- Values -->
      <div class="mb-[10px] flex items-stretch h-32">
        <div class="w-1/2">
          <img :src="Value" alt="Value" class="w-full h-full object-cover" />
        </div>
        <div class="w-1/2 bg-white p-3 shadow-sm flex flex-col justify-center">
          <h3 class="text-[4vw] font-bold text-blue-900 mb-2">Values</h3>
          <ul class="space-y-1">
            <li v-for="(val, key) in valueItems" :key="key" 
                class="text-[2.8vw] text-gray-700 leading-[1.4] flex items-start">
              <span class="text-blue-600 mr-1">•</span>
              {{ val }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Strengths -->
    <section class="bg-[#0B0B7C] py-8 px-4">
      <h2 class="text-[5vw] font-bold text-white mb-6">{{ aboutTexts.strengths?.title }}</h2>
      <div class="grid grid-cols-3 gap-2">
        <div v-for="(item, index) in strengthsData" :key="index"
             class="relative rounded-lg overflow-hidden shadow-lg" style="aspect-ratio: 1 / 2">
          <img :src="item.image" alt="Strength Image" class="absolute inset-0 w-full h-full object-cover" />
          <div class="absolute inset-0 flex flex-col justify-start items-center text-white p-2 pt-4"
               :style="{ background: item.color || 'rgba(0,0,0,0.7)' }">
            <h3 class="text-[3vw] font-bold text-center mb-1">{{ item.label }}</h3>
            <p class="text-[2.5vw] mt-2 text-center opacity-90 leading-[1.2]">{{ item.subTitle }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Certification -->
    <section class="h-64 w-full flex flex-col items-start justify-start bg-cover bg-center text-white px-4 pt-8"
             :style="{ backgroundImage: `url(${about_Certification})` }">
      <div class="text-left">
        <h2 class="text-[5vw] font-bold text-black">{{ aboutTexts.certification }}</h2>
      </div>
    </section>

    <!-- Gallery -->
    <section class="bg-[#F3F8FF]">
      <ImageGalleryForMobile :imageItems="ourStyles" :imagesPerPage="4" :columns="1" aspectRatio="4 / 3"/>
    </section>

    <!-- Action Buttons -->
    <ActionButtons />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import Mission from "../assets/Mission.png";
import Vision from "../assets/Vision.png";
import Value from "../assets/Value.png";
import about_Certification from "../assets/img_about_Certification.png";
import banner_about from "../assets/banner_about.jpg";
import { strengths, ourStyles } from "../utils/imageData";
import { pushToDataLayer } from "../utils/gtm";
import { useChatWidget } from "../utils/chatWidget";
import ActionButtons from "./ActionButtons.vue";

// 导入业务板块图标
import btn_about_1_nor from "../assets/btn_about_1_nor.png";
import btn_about_2_nor from "../assets/btn_about_2_nor.png";
import btn_about_3_nor from "../assets/btn_about_3_nor.png";
import btn_about_4_nor from "../assets/btn_about_4_nor.png";
import ImageGalleryForMobile from "./ImageGalleryForMobile.vue";

const { locale, messages } = useI18n();
const router = useRouter();

// 获取关于我们的文本
const aboutTexts = computed(() => {
  return messages.value[locale.value]?.about || {};
});

// 获取业务板块文本
const businessTexts = computed(() => {
  return messages.value[locale.value]?.businessSegments || {
    title: 'Business Segments'
  };
});

const valueItems = computed(() => aboutTexts.value.values?.items || []);

// 业务板块数据
const segments = computed(() => {
  const segmentsData = messages.value[locale.value]?.businessSegments?.segments;
  
  let segmentsArray = [];
  if (Array.isArray(segmentsData)) {
    segmentsArray = segmentsData;
  } else if (segmentsData && typeof segmentsData === 'object') {
    segmentsArray = Object.values(segmentsData);
  } else {
    return [];
  }
  
  return segmentsArray.map((segment, index) => ({
    ...segment,
    normalIcon: [btn_about_1_nor, btn_about_2_nor, btn_about_3_nor, btn_about_4_nor][index]
  }));
});

// 合并图片和国际化文本数据
const strengthsData = computed(() => {
  const textItems = aboutTexts.value.strengths?.items || [];
  return strengths.map((strengthItem, index) => ({
    image: strengthItem.image,
    label: textItems[index]?.label || strengthItem.label || '',
    subTitle: textItems[index]?.subTitle || strengthItem.subTitle || '',
    color: textItems[index]?.color || strengthItem.color
  })).filter(item => item.label && item.subTitle);
});

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("AboutUs");
  pushToDataLayer("Chat_With_Us", {
    category: "AboutUs",
    label: "Chat With Us",
  });
};
</script>

<style scoped>
.mobile-about-container {
  width: 100%;
  min-height: 100vh;
}
</style>
